import { useState } from 'react';
import { MetaTags } from '@/components/seo/meta-tags';
import { PageContainer } from '@/components/layout/page-container';
import { PageHero } from '@/components/layout/page-hero';
import { Reveal } from '@/components/motion/reveal';
import { Calendar } from './components/Calendar';
import { NoteForm } from './components/NoteForm';
import './contact.css';

// Contact in the house style: eyebrow, headline, a hairline segmented control, and the
// calendar shown straight away. No icons, no cards, no interstitial button.

type View = 'call' | 'note' | 'partner';

const views: { key: View; label: string }[] = [
  { key: 'call', label: 'Book a call' },
  { key: 'note', label: 'Send a note' },
  { key: 'partner', label: 'Partner network' },
];

export default function ContactPage() {
  const [view, setView] = useState<View>('call');

  return (
    <>
      <MetaTags
        title="Contact 7Sigma | Book a Call"
        description="Book a 30 minute discovery call with 7Sigma, or send a note. Fractional CTO, architecture, and app rescue. Orlando, FL. Remote, nationwide."
      />

      <PageHero
        eyebrow="Contact"
        title="Start a conversation."
        subtitle="Tell 7Sigma what you are building and where it is stuck. Thirty minutes on a call, or a note."
        mobileArt={false}
      />

      <PageContainer padTop={false}>
        {/* Segmented control */}
        <Reveal delay={0.05}>
          <div
            role="tablist"
            aria-label="How to reach 7Sigma"
            className="flex gap-6 sm:gap-8 border-b border-white/10 mb-8 md:mb-10 overflow-x-auto"
          >
            {views.map((v) => {
              const active = v.key === view;
              return (
                <button
                  key={v.key}
                  role="tab"
                  aria-selected={active}
                  onClick={() => setView(v.key)}
                  className={`relative pb-3 text-sm sm:text-base font-medium whitespace-nowrap transition-colors ${
                    active ? 'text-white' : 'text-white/55 hover:text-white'
                  }`}
                >
                  {v.label}
                  <span
                    className={`absolute left-0 right-0 -bottom-px h-[2px] transition-opacity ${
                      active ? 'bg-[#2563eb] opacity-100' : 'opacity-0'
                    }`}
                  />
                </button>
              );
            })}
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          {view === 'call' && (
            <Calendar
              onBooked="/contact/confirmation/discovery-call"
              note="Pick a time. A calendar invite with the meeting link follows."
            />
          )}
          {view === 'note' && <NoteForm />}
          {view === 'partner' && (
            <div>
              <p className="text-white/70 max-w-2xl leading-relaxed mb-8">
                7Sigma refers and works alongside senior engineers, designers, and technical
                leaders. Book a call, or email{' '}
                <a
                  href="mailto:discovery@7sigma.io"
                  className="text-white underline underline-offset-4"
                >
                  discovery@7sigma.io
                </a>
                .
              </p>
              <Calendar
                onBooked="/contact/confirmation/partner-network"
                note="Pick a time. A calendar invite follows."
              />
            </div>
          )}
        </Reveal>

        {/* What to expect, then direct lines. The calendar stays first so booking is above the fold. */}
        <Reveal delay={0.15}>
          <div className="mt-16 md:mt-20 border-t border-white/10 divide-y divide-white/10 max-w-3xl">
            {[
              ['01', 'The call', '30 minute discovery call, by video.'],
              ['02', 'Bring', 'What you are building, where it is stuck, and your timeline.'],
              ['03', 'Who', 'You talk to the principal. No sales team.'],
              [
                '04',
                'After',
                'A written proposal if there is a fit. A straight answer if there is not.',
              ],
              [
                '05',
                'Email',
                <a
                  key="e"
                  href="mailto:discovery@7sigma.io"
                  className="hover:text-[#2563eb] transition-colors"
                >
                  discovery@7sigma.io
                </a>,
              ],
              ['06', 'Based', 'Orlando, FL. Remote, nationwide.'],
            ].map(([n, k, v]) => (
              <div
                key={String(n)}
                className="py-4 md:py-5 grid grid-cols-[2.5rem_7rem_1fr] md:grid-cols-[3rem_9rem_1fr] gap-3 items-baseline"
              >
                <span className="text-sm md:text-base font-semibold text-white/40 tabular-nums">
                  {n}
                </span>
                <span className="text-sm text-white/55 uppercase tracking-[0.15em]">{k}</span>
                <span className="text-base md:text-lg text-white">{v}</span>
              </div>
            ))}
          </div>
        </Reveal>
      </PageContainer>
    </>
  );
}
